import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  Box, 
  Checkbox,
  Tooltip
} from '@mui/material';

import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

import { ReminderMessages, ReminderState } from '../../config/DiactiveTypes';

const RemindersPanel = ({ remindersData, isSinglePatient, onReminderUpdate }) => {
  const navigate = useNavigate();

  const [reminders, setReminders] = useState(remindersData);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setReminders(remindersData);
  }, [remindersData]);

  const handleToggle = (reminderId, isCompleted) => {
    // Convert checkbox state to reminder completion status
    onReminderUpdate(reminderId, isCompleted);
  };

  if (reminders.length === 0) {
    return null;
  }

  const sortedReminders = [...reminders].sort((a, b) => {
    // Sort by state: Active reminders first, then Completed
    if (a.state === b.state) return 0;
    return a.state === ReminderState.Completed ? 1 : -1;
  });

  // Count active reminders (not completed)
  const pendingCount = reminders.filter((reminder) => reminder.state === ReminderState.Active).length;

  return (
    <Card sx={{ mb: 4, border: 1.5, borderColor: 'primary.main', maxWidth: "sm", mx: 'auto' }}>
      {/* Expandable Box */}
      <Box 
        sx={{ py: 1.5, backgroundColor: 'primary.main', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body" fontWeight="medium" color="white">
            {pendingCount} recordatorio{pendingCount === 1 ? '' : 's'} pendiente{pendingCount === 1 ? '' : 's'}
          </Typography>
          {expanded ? (
            <ExpandLessIcon sx={{ color: 'white', ml: 1 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: 'white', ml: 1 }} />
          )}
        </Box>
      </Box>

      {expanded && (
        <List
          dense
          sx={{
            py: 0.3,
            px: 1,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {sortedReminders.map((reminder, index) => (
            <ListItem 
              key={reminder.id}
              divider={index < sortedReminders.length - 1}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                py: 0.3,
                px: 1
              }}
            >
              {/* Checkbox */}
              <Checkbox
                edge="start"
                color="primary"
                size="small"
                checked={reminder.state === ReminderState.Completed}
                onChange={(e) => handleToggle(reminder.id, e.target.checked)}
              />
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                      {!isSinglePatient && (
                        <Tooltip title={reminder.patientName} placement="top" enterDelay={300}>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 140,
                              flexShrink: 0,
                            }}
                          >
                            {reminder.patientName}:
                          </Typography>
                        </Tooltip>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0,
                        }}
                      >
                        {ReminderMessages[reminder.type]}
                      </Typography>
                      {!isSinglePatient && (
                        <ChevronRightIcon
                          sx={{ cursor: 'pointer', color: 'primary.main', ml: -0.5, flexShrink: 0 }}
                          onClick={() => navigate(`/patient/view/${reminder.patient}`)}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flexShrink: 0 }}
                    >
                      {reminder.activatedAtLocaleString}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Card>
  );
};

export default RemindersPanel;